import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Warehouse, Plus } from 'lucide-react';
import Button from '../../ui/Button';
import nurseryService from '../../../services/nursery';
import { errorMessage } from '../../../utils/apiResponse';
import { cn } from '../../../utils/cn';
import type { NurseryCrossDock } from '../../../types/nursery';

/**
 * The cross docks cross dock loads go to (2026-10-15): each with the address
 * printed on its manifest, the states whose stores go there unless their
 * customer says otherwise, and who its manifest is emailed to. A state goes
 * to one cross dock by default; the server refuses a second. Retired, never
 * deleted: a customer sent to a retired one falls back to its state's.
 */

const STATES = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'];
const parseEmails = (text: string) => [...new Set(text.split(/[\s,;]+/).map((e) => e.trim().toLowerCase()).filter(Boolean))];
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const inputClass = 'w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500';

type Draft = { name: string; address: string; states: string[]; recipients: string };
const BLANK: Draft = { name: '', address: '', states: [], recipients: '' };
const draftOf = (d: NurseryCrossDock): Draft => ({ name: d.name, address: d.address ?? '', states: d.states, recipients: d.recipients.join(', ') });

export function CrossDocksCard() {
  const { t } = useTranslation('nursery', { keyPrefix: 'CrossDocksCard' });
  const [docks, setDocks] = useState<NurseryCrossDock[] | null>(null);
  // The one being edited: an id, 'new', or none.
  const [editing, setEditing] = useState<number | 'new' | null>(null);
  const [draft, setDraft] = useState<Draft>(BLANK);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () => nurseryService.getCrossDocks(true)
    .then((res) => (res.success ? setDocks(res.data ?? []) : setError(errorMessage(res, t('readFailed')))))
    .catch((e) => setError(errorMessage(e, t('readFailed'))));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once; t is stable
  }, []);

  const emails = parseEmails(draft.recipients);
  const bad = emails.filter((e) => !EMAIL.test(e));
  // Which other cross dock already takes each state, so the box can say so.
  const takenBy = (state: string) => (docks ?? []).find((d) => d.active && d.id !== editing && d.states.includes(state));

  const start = (d: NurseryCrossDock | null) => {
    setError(null);
    setEditing(d ? d.id : 'new');
    setDraft(d ? draftOf(d) : BLANK);
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = { name: draft.name.trim(), address: draft.address.trim() || null, states: draft.states, recipients: emails };
      const res = editing === 'new'
        ? await nurseryService.createCrossDock(body)
        : await nurseryService.updateCrossDock(editing as number, body);
      if (!res.success) { setError(errorMessage(res, t('saveFailed'))); return; }
      setEditing(null);
      await load();
    } catch (e) {
      setError(errorMessage(e, t('saveFailed')));
    } finally {
      setBusy(false);
    }
  };

  const setActive = async (d: NurseryCrossDock, active: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryService.updateCrossDock(d.id, { active });
      if (!res.success) setError(errorMessage(res, t('saveFailed')));
      await load();
    } catch (e) {
      setError(errorMessage(e, t('saveFailed')));
    } finally {
      setBusy(false);
    }
  };

  const form = (
    <div className="border border-primary-200 bg-primary-50/40 rounded-lg p-3 space-y-3">
      <label className="block text-sm">
        <span className="text-neutral-700">{t('name')}</span>
        <input type="text" className={`${inputClass} mt-1`} value={draft.name} placeholder={t('namePlaceholder')}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
      </label>
      <label className="block text-sm">
        <span className="text-neutral-700">{t('address')}</span>
        <textarea rows={3} className={`${inputClass} mt-1`} value={draft.address} placeholder={t('addressPlaceholder')}
          onChange={(e) => setDraft({ ...draft, address: e.target.value })} />
        <span className="text-xs text-neutral-500">{t('addressHelp')}</span>
      </label>
      <fieldset className="text-sm">
        <legend className="text-neutral-700">{t('states')}</legend>
        <div className="flex flex-wrap gap-2 mt-1">
          {STATES.map((s) => {
            const other = takenBy(s);
            const on = draft.states.includes(s);
            return (
              <label key={s} className={cn('inline-flex items-center gap-1.5 rounded-lg border px-2 py-1',
                on ? 'border-primary-400 bg-white' : 'border-neutral-200', other && 'opacity-60')}
                title={other ? t('takenBy', { name: other.name }) : undefined}>
                <input type="checkbox" checked={on} disabled={!!other}
                  onChange={(e) => setDraft({ ...draft, states: e.target.checked ? [...draft.states, s].sort() : draft.states.filter((x) => x !== s) })} />
                {s}
              </label>
            );
          })}
        </div>
        <span className="block text-xs text-neutral-500 mt-1 break-words">{t('statesHelp')}</span>
      </fieldset>
      <label className="block text-sm">
        <span className="text-neutral-700">{t('recipients')}</span>
        <textarea rows={2} className={`${inputClass} mt-1`} value={draft.recipients} placeholder="bookings@plantaccess.com.au"
          onChange={(e) => setDraft({ ...draft, recipients: e.target.value })} />
        {bad.length > 0 && <span className="block text-sm text-red-700 break-words">{t('notEmail', { list: bad.join(', ') })}</span>}
      </label>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={save} disabled={busy || !draft.name.trim() || bad.length > 0}>{busy ? t('saving') : t('save')}</Button>
        <Button size="sm" variant="outline" onClick={() => setEditing(null)} disabled={busy}>{t('cancel')}</Button>
      </div>
    </div>
  );

  return (
    <section aria-label={t('title')} className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
      <div className="flex items-start gap-2">
        <Warehouse className="h-4 w-4 mt-0.5 text-neutral-500 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-neutral-800">{t('title')}</h2>
          <p className="text-sm text-neutral-600">{t('description')}</p>
        </div>
        {editing === null && (
          <Button size="sm" variant="outline" onClick={() => start(null)}>
            <Plus className="h-4 w-4" /> {t('add')}
          </Button>
        )}
      </div>
      {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 break-words">{error}</div>}
      {editing === 'new' && form}
      {docks === null ? (
        !error && <div className="h-16 bg-neutral-100 rounded-lg animate-pulse" />
      ) : docks.length === 0 && editing !== 'new' ? (
        <p className="text-sm text-neutral-500">{t('none')}</p>
      ) : (
        <ul className="space-y-2">
          {docks.map((d) => (editing === d.id ? <li key={d.id}>{form}</li> : (
            <li key={d.id} className={cn('border border-neutral-200 rounded-lg p-3', !d.active && 'bg-neutral-50')}>
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="font-medium text-neutral-900 break-words min-w-0">{d.name}</span>
                {!d.active && <span className="text-xs text-neutral-500">{t('retired')}</span>}
                <span className="text-sm text-neutral-600">
                  {d.states.length ? t('takesStates', { states: d.states.join(', ') }) : t('takesNone')}
                </span>
              </div>
              <p className="text-xs text-neutral-500 break-words mt-0.5">
                {d.recipients.length ? t('sendsTo', { list: d.recipients.join(', ') }) : t('sendsToNobody')}
                {d.customer_count > 0 ? ` · ${t('customers', { count: d.customer_count })}` : ''}
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                {d.active && <Button size="sm" variant="outline" onClick={() => start(d)} disabled={editing !== null || busy}>{t('edit')}</Button>}
                <Button size="sm" variant="ghost" onClick={() => setActive(d, !d.active)} disabled={editing !== null || busy}>
                  {d.active ? t('retire') : t('restore')}
                </Button>
              </div>
            </li>
          )))}
        </ul>
      )}
    </section>
  );
}

export default CrossDocksCard;
